import type { CSSProperties, FunctionComponent } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Stack, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

import TeamworkGraphIllustration from '#assets/illustrations/twg/teamwork-graph-cli.png';
import Headline from './Headline';
import { TWG_CLI_LEARN_MORE_URL } from './constants';

const messages = defineMessages({
  title: {
    id: 'oauth.twg.adminRail.title',
    defaultMessage: 'What is Teamwork Graph CLI?',
    description: 'Title for the TWG CLI informational rail',
  },
  body: {
    id: 'oauth.twg.adminRail.body',
    defaultMessage:
      'Teamwork Graph brings your work context to where you code - the terminal. Analyze massive logs, refine release notes, and debug complex codebases with connected insights across all your applications.',
    description:
      'Body text explaining Teamwork Graph CLI on the consent screen',
  },
  learnMore: {
    id: 'oauth.twg.adminRail.learnMore',
    defaultMessage: 'Learn more',
    description: 'Link text for Teamwork Graph CLI documentation',
  },
});

const railStyle = xcss({
  width: '100%',
});

const styles: Record<string, CSSProperties> = {
  rail: {
    color: 'var(--twg-text)',
  },
  infoCard: {
    borderColor: 'var(--twg-card-border)',
    borderRadius: '8px',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxShadow: 'none',
    overflow: 'hidden',
    width: '100%',
  },
  imageWrap: {
    alignItems: 'center',
    backgroundColor: 'var(--twg-illustration-bg)',
    display: 'flex',
    height: '121px',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  illustration: {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  infoBody: {
    backgroundColor: 'var(--twg-info-panel-bg)',
    color: 'var(--twg-info-panel-text)',
    paddingBlock: '16px',
    paddingInline: '16px',
  },
  infoTitle: {
    color: 'var(--twg-info-panel-text)',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px',
    margin: 0,
  },
  infoParagraph: {
    color: 'var(--twg-info-panel-muted)',
    fontSize: '12px',
    lineHeight: '16px',
    margin: 0,
  },
  infoActions: {
    margin: 0,
  },
};

const TwgAdminRail: FunctionComponent = () => (
  <Stack xcss={railStyle} space="space.500">
    <Headline />
    <div style={styles.rail}>
      <div style={styles.infoCard}>
        <div style={styles.imageWrap}>
          <img
            src={TeamworkGraphIllustration}
            alt=""
            aria-hidden="true"
            style={styles.illustration}
          />
        </div>
        <div style={styles.infoBody}>
          <Stack space="space.200">
            <h2 style={styles.infoTitle}>
              <FormattedMessage {...messages.title} />
            </h2>
            <p style={styles.infoParagraph}>
              <FormattedMessage {...messages.body} />
            </p>
            <p style={styles.infoActions}>
              <Link
                href={TWG_CLI_LEARN_MORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage {...messages.learnMore} />
              </Link>
            </p>
          </Stack>
        </div>
      </div>
    </div>
  </Stack>
);

export default TwgAdminRail;
